/**
 * SmsConfirmationModal Component
 * SESSION_201: Modal shown when user tries to complete onboarding without SMS
 *
 * Prompts the user to enable SMS notifications for faster alerts.
 * Includes phone number input so user can easily enable SMS from the modal.
 */

import React, { useCallback, useState } from 'react';
import {
  Modal,
  Stack,
  Text,
  Button,
  Group,
  TextInput,
  Alert
} from '@mantine/core';
import { IconPhone, IconMessageCircle, IconInfoCircle } from '@tabler/icons-react';
import type { SmsConfirmationModalProps } from './SmsConfirmationModal.types';
import {
  isValidPhoneNumber,
  formatPhoneAsYouType
} from '../../../../utils/phoneValidation';

/**
 * SmsConfirmationModal - Confirmation dialog for enabling SMS notifications
 */
export const SmsConfirmationModal: React.FC<SmsConfirmationModalProps> = ({
  opened,
  onClose,
  onEnableSms,
  onContinueWithoutSms,
  isLoading = false
}): React.ReactElement => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState<string | undefined>();

  /**
   * Handle phone number change with auto-formatting
   */
  const handlePhoneChange = useCallback((value: string): void => {
    const formatted = formatPhoneAsYouType(value);
    setPhoneNumber(formatted);
    // Clear error when user starts typing
    if (phoneError) {
      setPhoneError(undefined);
    }
  }, [phoneError]);

  /**
   * Handle Enable SMS button click
   */
  const handleEnableSms = useCallback((): void => {
    // Validate phone number
    if (!phoneNumber.trim()) {
      setPhoneError('Please enter your phone number to enable SMS notifications');
      return;
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      setPhoneError('Please enter a valid US phone number (e.g., (206) 555-1234)');
      return;
    }

    onEnableSms(phoneNumber);
  }, [phoneNumber, onEnableSms]);

  /**
   * Handle Continue Without SMS button click
   */
  const handleContinueWithout = useCallback((): void => {
    onContinueWithoutSms();
  }, [onContinueWithoutSms]);

  /**
   * Reset state when modal closes
   */
  const handleClose = useCallback((): void => {
    setPhoneNumber('');
    setPhoneError(undefined);
    onClose();
  }, [onClose]);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconMessageCircle size={24} color="var(--mantine-color-blue-6)" />
          <Text fw={600} size="lg">Enable SMS Notifications?</Text>
        </Group>
      }
      centered
      size="md"
    >
      <Stack gap="lg">
        {/* Info Alert */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          For the fastest alerts when your business is mentioned online, enable SMS notifications.
          You can change this anytime from your settings.
        </Alert>

        {/* Phone Number Input */}
        <TextInput
          label="Phone Number"
          placeholder="(206) 555-1234"
          description="Enter your phone number to receive instant text alerts"
          leftSection={<IconPhone size={16} />}
          value={phoneNumber}
          onChange={(e) => handlePhoneChange(e.currentTarget.value)}
          error={phoneError}
          disabled={isLoading}
        />

        {/* Action Buttons */}
        <Group justify="flex-end" mt="md">
          <Button
            variant="subtle"
            color="gray"
            onClick={handleContinueWithout}
            disabled={isLoading}
          >
            Continue without SMS
          </Button>
          <Button
            onClick={handleEnableSms}
            loading={isLoading}
            leftSection={<IconMessageCircle size={16} />}
          >
            Enable SMS
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
